// THE BASS SHAKE, PREVIEWED (owner 2026-10-27: "it would be better if that happened at the same time they choose their
// song... that way they can choose the section with the bass effect they want and see it throughout the creation
// instead of at the very end after its been rendered"): the song part's bass loudness every 10 ms, worked out in the
// browser with the server's own numbers (canvas.js lowpass + bassLevel), so the editor's picture shakes as the file will.

/** Two-pole low-pass (canvas.js lowpass, the same biquad). */
function lowpass(x: Float32Array, rate: number, cutoff: number): Float32Array {
  const w = (2 * Math.PI * cutoff) / rate;
  const alpha = Math.sin(w) / (2 * Math.SQRT1_2);
  const cw = Math.cos(w);
  const a0 = 1 + alpha;
  const b0 = (1 - cw) / 2 / a0; const b1 = (1 - cw) / a0; const b2 = b0;
  const a1 = (-2 * cw) / a0; const a2 = (1 - alpha) / a0;
  const y = new Float32Array(x.length);
  let x1 = 0; let x2 = 0; let y1 = 0; let y2 = 0;
  for (let i = 0; i < x.length; i++) {
    const v = b0 * x[i] + b1 * x1 + b2 * x2 - a1 * y1 - a2 * y2;
    x2 = x1; x1 = x[i]; y2 = y1; y1 = v; y[i] = v;
  }
  return y;
}

/** canvas.js bassLevel, the same numbers: 0..1 every 10 ms, as loud as the bass under `cutoff` Hz against the part's loudest. */
export function bassLevel(samples: Float32Array, rate: number, { cutoff = 140, release = 0.05, floor = 0.12, punch = 0.6 } = {}): Float32Array {
  const x = lowpass(lowpass(samples, rate, cutoff), rate, cutoff);
  const hop = Math.max(1, Math.round(rate * 0.01));
  const n = Math.floor(x.length / hop);
  const env = new Float32Array(n);
  const fall = Math.exp(-0.01 / release);
  let held = 0;
  for (let i = 0; i < n; i++) {
    let sum = 0;
    const end = Math.min(x.length, (i + 3) * hop);
    for (let j = i * hop; j < end; j++) sum += x[j] * x[j];
    const v = Math.sqrt(sum / Math.max(1, end - i * hop));
    held = v > held ? v : held * fall + v * (1 - fall);
    env[i] = held;
  }
  const out = new Float32Array(n);
  if (!n) return out;
  const sorted = Float32Array.from(env).sort();
  const hi = sorted[Math.floor(0.98 * (n - 1))];
  const lo = hi * floor;
  if (!(hi > lo) || !(hi > 1e-4)) return out;
  const jump = new Float32Array(n);
  let top = 0;
  for (let i = 3; i < n; i++) { jump[i] = Math.max(0, env[i] - env[i - 3]); if (jump[i] > top) top = jump[i]; }
  let kick = 0;
  for (let i = 0; i < n; i++) {
    const base = Math.min(1, Math.max(0, (env[i] - lo) / (hi - lo))) ** 1.2;
    kick = Math.max(kick * 0.8, top > 0 ? jump[i] / top : 0);
    out[i] = base > 0 ? Math.min(1, base * (1 - punch * 0.4) + punch * kick) : 0;
  }
  return out;
}

/** The song's bass level over `len` seconds from `start` (the part picked), mono at 8 kHz as the server reads it. */
export async function songBassLevel(file: File, start: number, len: number): Promise<Float32Array> {
  const RATE = 8000;
  const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const ctx = new Ctx();
  try {
    const audio = await ctx.decodeAudioData(await file.arrayBuffer());
    const from = Math.max(0, Math.min(audio.duration, start));
    const span = Math.max(0.5, Math.min(len, audio.duration - from));
    // Down to 8 kHz mono (the server's BEAT_RATE) by the browser's own resampler.
    const off = new OfflineAudioContext(1, Math.ceil(span * RATE), RATE);
    const src = off.createBufferSource();
    src.buffer = audio;
    src.connect(off.destination);
    src.start(0, from, span);
    const mono = (await off.startRendering()).getChannelData(0);
    return bassLevel(mono, RATE);
  } finally {
    void ctx.close();
  }
}
